/**
 * Open an Inbox thread's linked ai_conversation in chat (agent panel / editor).
 *
 * Unlike openAgentChatThread alone (which only binds the thread host), this also
 * dispatches open-chat-conversation with the linked conversation id so the pane
 * cannot keep streaming into a stale "Ingesting: ..." conversation.
 *
 * See: docs/product/incidents/2026-07-21-inbox-chat-desync-forcerreplace-no-diff.md
 */

import { openAgentChatThread } from '@/lib/open-agent-chat-thread'
import { refreshChatsSidebar } from '@/lib/chats-sidebar-events'
import type {
  ConversationDraft,
  OpenConversationOptions,
} from '@/hooks/use-chat-surface-registry'

type RegistryLike = {
  openThread: (threadId: string, options?: OpenConversationOptions) => void
  openInEditor?: boolean
  getDraft?: (threadId: string) => ConversationDraft
  setDraft?: (threadId: string, draft: ConversationDraft) => void
} | null | undefined

export type OpenInboxThreadInChatOptions = {
  threadId: string
  title?: string
  /** Known linked conversation; when missing, hydrate is required. */
  linkedConversationId?: string | null
  hydrate?: (threadId: string) => Promise<string | null>
  registry?: RegistryLike
  /** Prefer agent panel (default true for Inbox "Open in chat"). */
  forceAgentPanel?: boolean
  /** Attach a document context chip to the bound thread's composer draft. */
  contextDocumentId?: string
  contextDocumentTitle?: string
}

function dispatchOpenConversation(
  conversationId: string,
  threadId: string,
  targetSurface: 'agent-panel' | 'editor',
): void {
  window.dispatchEvent(
    new CustomEvent('open-chat-conversation', {
      detail: {
        conversationId,
        targetThreadId: threadId,
        targetSurface,
        autoScroll: true,
      },
    }),
  )
}

/**
 * Hydrate linked conversation if needed, bind the thread surface, then force-load
 * that conversation into the matching ChatPane.
 * @returns linked conversation id, or null on failure
 */
export async function openInboxThreadInChat(
  options: OpenInboxThreadInChatOptions,
): Promise<string | null> {
  const {
    threadId,
    title,
    hydrate,
    registry,
    forceAgentPanel = true,
    contextDocumentId,
    contextDocumentTitle,
  } = options

  let linked = options.linkedConversationId ?? null
  if (!linked && hydrate) {
    linked = await hydrate(threadId)
  }
  if (!linked) return null

  const targetSurface: 'agent-panel' | 'editor' =
    forceAgentPanel || registry?.openInEditor === false ? 'agent-panel' : 'editor'

  refreshChatsSidebar()
  openAgentChatThread(registry, threadId, {
    title,
    ...(forceAgentPanel ? { forceAgentPanel: true } : {}),
  })

  if (contextDocumentId && registry?.setDraft) {
    const existing = registry.getDraft?.(threadId)
    registry.setDraft(threadId, {
      text: existing?.text ?? '',
      references: existing?.references ?? [],
      images: existing?.images ?? [],
      contextDocumentId,
      contextDocumentTitle: contextDocumentTitle ?? existing?.contextDocumentTitle,
    })
  }

  // Immediate: works when the agent panel already hosts this thread (early-return
  // path in openInAgentPanel) - the desync case where chip shows inbox but
  // currentConversationId is still an Ingesting: chat.
  dispatchOpenConversation(linked, threadId, targetSurface)

  // Deferred: works when openThread just switched hostedThreadId and React has
  // not painted yet (useOpenConversationListener filters on hostedThreadId).
  window.setTimeout(() => {
    dispatchOpenConversation(linked!, threadId, targetSurface)
  }, 80)

  return linked
}

/** Scoped auto-send into a specific hosted inbox/agent thread (ignores foreign panes). */
export function dispatchScopedChatAutoSend(
  message: string,
  targetThreadId: string,
  extras?: { viewContext?: unknown },
): void {
  window.dispatchEvent(
    new CustomEvent('chat-auto-send', {
      detail: {
        message,
        targetThreadId,
        ...(extras?.viewContext ? { viewContext: extras.viewContext } : {}),
      },
    }),
  )
}
